/**
 * ekdum-fresh-page.js
 * Asset: Ekdum Fresh Collection Page – Interactive Scripts
 * Loaded at template level via sections/ekdum-fresh-assets.liquid
 *
 * Responsibilities:
 * 1. Drag-scroll on the date button container
 * 2. Day button smooth-scroll navigation
 * 3. Date-wise section limit (show first 6, reveal rest via button)
 * 4. Collection card scrollbar thumb position sync
 * 5. Sticky date filter bar (sticks while date-wise sections are on screen)
 * 6. Date sync: sort date sections newest-first, hide buttons without a section
 *
 * Rules followed (AGENTS.md):
 * - All DOM queries use data-* attributes, never class/tag selectors
 * - No global variables or functions (all wrapped in IIFE)
 * - All state changes are explicit
 * - Observers and listeners are set up once, no polling
 * - Silent failures are forbidden; all DOM access is guarded
 * - Business logic is pure; DOM logic only reads/applies
 */

(function initEkdumFreshPage() {
  'use strict';

  /* --------------------------------------------------------------------------
   * 1. DRAG-SCROLL – Date button container
   * data-date-btn-container is on .other_dates_container
   * -------------------------------------------------------------------------- */
  function initDragScroll(container) {
    if (!container) return;

    var DRAG_THRESHOLD = 6;
    var isPressed = false;
    var hasDragged = false;
    var startX = 0;
    var scrollStartLeft = 0;
    var pointerId = null;

    function endDrag() {
      if (!isPressed) return;
      isPressed = false;
      container.classList.remove('is-dragging');
      if (pointerId !== null && container.hasPointerCapture && container.hasPointerCapture(pointerId)) {
        container.releasePointerCapture(pointerId);
      }
      pointerId = null;
    }

    function onPointerDown(e) {
      // Touch and pen already scroll natively; only handle a primary mouse press
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      isPressed = true;
      hasDragged = false;
      startX = e.clientX;
      scrollStartLeft = container.scrollLeft;
      pointerId = e.pointerId;
    }

    function onPointerMove(e) {
      if (!isPressed) return;
      // Safety net: button was released somewhere we did not see (outside window, etc.)
      if (e.buttons === 0) {
        endDrag();
        return;
      }
      var delta = e.clientX - startX;
      if (!hasDragged && Math.abs(delta) > DRAG_THRESHOLD) {
        hasDragged = true;
        container.classList.add('is-dragging');
        // Capture so move/up keep firing even if the pointer leaves the bar
        if (container.setPointerCapture) container.setPointerCapture(pointerId);
      }
      if (hasDragged) container.scrollLeft = scrollStartLeft - delta;
    }

    // A drag that ends over a button must not trigger that button's link
    function onClickCapture(e) {
      if (!hasDragged) return;
      e.preventDefault();
      e.stopPropagation();
      hasDragged = false;
    }

    // Native link/image drag-and-drop swallows mouseup and left the old
    // implementation stuck in "dragging"; disable it inside the bar.
    function onDragStart(e) {
      e.preventDefault();
    }

    container.addEventListener('pointerdown', onPointerDown);
    container.addEventListener('pointermove', onPointerMove);
    container.addEventListener('pointerup', endDrag);
    container.addEventListener('pointercancel', endDrag);
    container.addEventListener('lostpointercapture', endDrag);
    container.addEventListener('dragstart', onDragStart);
    container.addEventListener('click', onClickCapture, true);
    window.addEventListener('blur', endDrag);
  }

  /* --------------------------------------------------------------------------
   * 2. DAY BUTTON SMOOTH-SCROLL NAVIGATION
   * data-date-nav-link is on every <a> inside today_btn and other_date_btn
   * -------------------------------------------------------------------------- */
  var STUCK_CLASS = 'date_btn--stuck';
  var NAV_GAP = 16;
  var NAV_MAX_CORRECTIONS = 3;

  // Header takes space at the top only while visible. It hides on scroll down
  // (unless configured to always stay), so the offset depends on direction.
  function getHeaderOffset(goingDown) {
    var stickyHeader = document.querySelector('sticky-header');
    var header = document.querySelector('.header-wrapper');
    if (!header) return 0;
    var type = stickyHeader ? stickyHeader.getAttribute('data-sticky-type') : 'none';
    var alwaysVisible = type === 'always' || type === 'reduce-logo-size';
    if (goingDown && !alwaysVisible) return 0;
    return header.offsetHeight || 0;
  }

  // The bar is taller when stuck (extra padding, different margins) than in
  // flow. Always measure the stuck height, otherwise the first scroll overshoots
  // and then jumps back once the bar sticks and the height is corrected.
  function getBarOffset() {
    var bar = document.querySelector('[data-date-bar]');
    if (!bar) return 0;
    if (bar.classList.contains(STUCK_CLASS)) return bar.offsetHeight;

    bar.classList.add(STUCK_CLASS);
    var height = bar.offsetHeight;
    bar.classList.remove(STUCK_CLASS);
    return height;
  }

  function computeScrollY(targetEl, goingDown) {
    var top = targetEl.getBoundingClientRect().top + window.pageYOffset;
    return Math.max(0, top - getHeaderOffset(goingDown) - getBarOffset() - NAV_GAP);
  }

  // Lazy images and "View More" shift the layout while the smooth scroll runs,
  // so once scrolling settles we re-measure and correct, a few times at most.
  function scrollToDateSection(targetEl) {
    var goingDown = targetEl.getBoundingClientRect().top > 0;
    var corrections = 0;
    var settleTimer = null;

    function cancel() {
      window.clearTimeout(settleTimer);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('wheel', cancel);
      window.removeEventListener('touchstart', cancel);
      window.removeEventListener('keydown', cancel);
    }

    function settle() {
      var y = computeScrollY(targetEl, goingDown);
      if (Math.abs(y - window.pageYOffset) > 4 && corrections < NAV_MAX_CORRECTIONS) {
        corrections++;
        window.scrollTo({ top: y, behavior: 'smooth' });
        return;
      }
      cancel();
    }

    function onScroll() {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(settle, 160);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('wheel', cancel, { passive: true });
    window.addEventListener('touchstart', cancel, { passive: true });
    window.addEventListener('keydown', cancel);

    window.scrollTo({ top: computeScrollY(targetEl, goingDown), behavior: 'smooth' });
  }

  function initDayNavLinks() {
    var links = document.querySelectorAll('[data-date-nav-link]');
    links.forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();

        var href = link.getAttribute('href');
        if (!href || href.length < 2) return;

        var targetEl = document.getElementById(href.substring(1));
        if (!targetEl) return;

        // Only reveal if the target section is hidden behind "View More"
        var parentSection = targetEl.closest('.shopify-section') || targetEl;
        if (parentSection.classList.contains('day_section_hidden') || targetEl.classList.contains('day_section_hidden')) {
          revealAllDaySections();
        }

        scrollToDateSection(targetEl);
      });
    });
  }

  /* --------------------------------------------------------------------------
   * 3. DATE-WISE SECTION LIMIT
   * data-date-wise-section is on each .shopify-section wrapper
   * -------------------------------------------------------------------------- */
  function getDateWiseSections() {
    var rawSections = document.querySelectorAll('[data-date-wise-collection]');
    var sections = [];
    rawSections.forEach(function (sec) {
      var parentSection = sec.closest('.shopify-section') || sec;
      if (!sections.includes(parentSection)) {
        sections.push(parentSection);
      }
    });
    return sections;
  }

  function revealAllDaySections() {
    var hidden = document.querySelectorAll('.shopify-section.day_section_hidden, .day_section_hidden');
    hidden.forEach(function (sec) {
      sec.classList.remove('day_section_hidden');
    });

    var btnWrapper = document.querySelector('[data-view-more-wrapper]');
    if (btnWrapper) btnWrapper.remove();

    var sections = getDateWiseSections();
    if (!sections.length) return;

    var lastSection = sections[sections.length - 1];
    var divider = document.querySelector('.collection-section-divider');
    if (divider && lastSection) {
      lastSection.after(divider);
    }
  }

  function initDateWiseSectionsLimit() {
    var sections = getDateWiseSections();
    if (!sections.length) return false;

    if (sections.length > 6) {
      for (var i = 6; i < sections.length; i++) {
        sections[i].classList.add('day_section_hidden');
      }

      if (!document.querySelector('[data-view-more-wrapper]')) {
        var btnWrapper = document.createElement('div');
        btnWrapper.className = 'date_sections_view_more_wrapper';
        btnWrapper.setAttribute('data-view-more-wrapper', '');
        btnWrapper.innerHTML =
          '<button type="button" class="date_sections_view_more_btn" data-view-more-btn>View More</button>';

        var viewMoreBtn = btnWrapper.querySelector('[data-view-more-btn]');
        if (viewMoreBtn) {
          viewMoreBtn.addEventListener('click', revealAllDaySections);
        }

        sections[5].after(btnWrapper);
      }
    }

    var divider = document.querySelector('.collection-section-divider');
    if (!divider) {
      divider = document.createElement('hr');
      divider.className = 'collection-section-divider';
    }

    var anchor = document.querySelector('[data-view-more-wrapper]') || sections[sections.length - 1];
    if (anchor) {
      anchor.after(divider);
    }

    return true;
  }

  /* --------------------------------------------------------------------------
   * 4. SCROLLBAR THUMB – Collection card horizontal scroll indicator
   * data-collection-cards is on .custom_cards
   * data-scrollbar-track and data-scrollbar-thumb on the track/thumb divs
   * -------------------------------------------------------------------------- */
  function updateSectionThumb(sectionEl) {
    if (!sectionEl) return;

    var cards = sectionEl.querySelector('[data-collection-cards]');
    var track = sectionEl.querySelector('[data-scrollbar-track]');
    var thumb = sectionEl.querySelector('[data-scrollbar-thumb]');

    if (!cards || !track || !thumb) return;

    var maxScroll = cards.scrollWidth - cards.clientWidth;
    var trackWidth = track.clientWidth || cards.clientWidth || 362;
    var thumbWidth = 155;
    var maxTranslate = Math.max(0, trackWidth - thumbWidth);

    if (maxScroll <= 5) {
      thumb.style.transform = 'translateX(0px)';
      return;
    }

    var scrollRatio = Math.min(1, Math.max(0, cards.scrollLeft / maxScroll));
    var translateX = scrollRatio * maxTranslate;
    thumb.style.transform = 'translateX(' + translateX + 'px)';
  }

  function handleCardScroll(e) {
    var target = e.target;
    if (!target || typeof target.closest !== 'function') return;
    var cards = target.closest('[data-collection-cards]');
    if (!cards) return;

    var section = target.closest('[data-date-wise-collection]') || target.closest('.shopify-section');
    if (section) {
      updateSectionThumb(section);
    }
  }

  function initAllScrollbarThumbs() {
    var sections = document.querySelectorAll('[data-date-wise-collection]');
    sections.forEach(function (sec) {
      var parentSec = sec.closest('.shopify-section') || sec;
      updateSectionThumb(parentSec);
    });
  }

  /* --------------------------------------------------------------------------
   * 5. STICKY DATE BAR
   * data-date-bar is on the date filter <section>. The bar is pinned (fixed)
   * once its natural position scrolls past the top, and released when the
   * last visible date-wise section ends. A placeholder keeps layout height.
   * -------------------------------------------------------------------------- */
  function initStickyDateBar() {
    var bar = document.querySelector('[data-date-bar]');
    if (!bar) return;

    var wrapper = bar.closest('.shopify-section') || bar.parentElement;
    if (!wrapper) return;

    var GAP_TOP = 0;
    var GAP_BOTTOM = 0;
    var isStuck = false;
    var ticking = false;
    var settleTimer = null;

    function getHeaderBottom() {
      var header = document.querySelector('.header-wrapper');
      if (!header) return 0;
      var rect = header.getBoundingClientRect();
      // Header only takes space at the top while it is visible on screen
      return Math.max(0, rect.bottom);
    }

    function getLastVisibleDaySection() {
      var sections = getDateWiseSections();
      for (var i = sections.length - 1; i >= 0; i--) {
        if (sections[i].offsetParent !== null) return sections[i];
      }
      return null;
    }

    function release() {
      if (!isStuck) return;
      bar.classList.remove(STUCK_CLASS);
      bar.style.top = '';
      wrapper.style.minHeight = '';
      isStuck = false;
    }

    function update() {
      ticking = false;
      var lastSection = getLastVisibleDaySection();
      if (!lastSection) {
        release();
        return;
      }

      var headerBottom = getHeaderBottom();
      var barHeight = isStuck ? bar.offsetHeight : bar.getBoundingClientRect().height;
      var wrapperRect = wrapper.getBoundingClientRect();
      var lastRect = lastSection.getBoundingClientRect();

      var stickTop = headerBottom + GAP_TOP;
      // Natural top of the bar: the wrapper's top edge (placeholder when stuck)
      var shouldStick = wrapperRect.top <= stickTop && lastRect.bottom > 0;

      if (!shouldStick) {
        release();
        return;
      }

      if (!isStuck) {
        wrapper.style.minHeight = wrapperRect.height + 'px';
        bar.classList.add(STUCK_CLASS);
        isStuck = true;
        barHeight = bar.offsetHeight;
      }

      // Push the bar up with the end of the date sections so it never overlaps what follows
      var top = Math.min(stickTop, lastRect.bottom - barHeight - GAP_BOTTOM);
      bar.style.top = top + 'px';
    }

    function requestUpdate() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
      // Header slide animation finishes after scrolling stops; re-check once
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(requestUpdate, 350);
    }

    function onResize() {
      release();
      requestUpdate();
    }

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('load', requestUpdate);
    requestUpdate();
  }

  /* --------------------------------------------------------------------------
   * 6. DATE SYNC
   * Buttons and sections are matched purely by date (#date-YYYY-MM-DD).
   * - Sections are ordered newest-first so page order matches the bar.
   * - Buttons with no matching section are hidden (and warned about).
   * -------------------------------------------------------------------------- */
  function sortDateSections() {
    var wrappers = getDateWiseSections().filter(function (wrapper) {
      var sec = wrapper.querySelector('[data-date]');
      return sec && sec.getAttribute('data-date');
    });
    if (wrappers.length < 2) return;

    function dateOf(wrapper) {
      return wrapper.querySelector('[data-date]').getAttribute('data-date');
    }

    var sorted = wrappers.slice().sort(function (a, b) {
      var da = dateOf(a);
      var db = dateOf(b);
      return da < db ? 1 : da > db ? -1 : 0; // ISO dates sort lexically
    });

    var unchanged = sorted.every(function (el, i) { return el === wrappers[i]; });
    if (unchanged) return;

    var anchor = document.createComment('date-sections');
    wrappers[0].before(anchor);
    sorted.forEach(function (wrapper) {
      anchor.after(wrapper);
      anchor = wrapper;
    });
  }

  function pruneDateButtons() {
    var scrollArea = document.querySelector('[data-date-scroll-area]');
    if (!scrollArea) return;

    var buttons = Array.prototype.slice.call(scrollArea.querySelectorAll('[data-date-btn]'));
    var kept = buttons.filter(function (btn) {
      var link = btn.querySelector('[data-date-nav-link]');
      var href = link ? link.getAttribute('href') : '';
      var exists = href && href.length > 1 && document.getElementById(href.substring(1));
      if (!exists && window.console) {
        console.warn('[ekdum-fresh] No date section found for button', href);
      }
      return exists;
    });
    if (kept.length === buttons.length) return;

    // Rebuild: first kept button is the highlighted pill, the rest share one scroll container
    var container = document.createElement('div');
    container.className = 'other_dates_container';
    container.setAttribute('data-date-btn-container', '');

    scrollArea.innerHTML = '';
    kept.forEach(function (btn, i) {
      if (i === 0) {
        btn.className = 'today_btn';
        scrollArea.appendChild(btn);
        return;
      }
      if (i > 1) {
        var sep = document.createElement('div');
        sep.className = 'date_separator';
        container.appendChild(sep);
      }
      btn.className = 'other_date_btn';
      container.appendChild(btn);
    });
    if (kept.length > 1) scrollArea.appendChild(container);
  }

  /* --------------------------------------------------------------------------
   * BOOTSTRAP – Set everything up after DOM is ready
   * Uses DOMContentLoaded + MutationObserver for resilience (no polling).
   * -------------------------------------------------------------------------- */
  function bootstrap() {
    // 0. Date sync must run first: it reorders sections and rebuilds buttons
    sortDateSections();
    pruneDateButtons();

    // 1. Drag scroll for date buttons and explore categories
    var dateBtnContainer = document.querySelector('[data-date-btn-container]');
    initDragScroll(dateBtnContainer);

    var exploreCategoryGrid = document.querySelector('[data-explore-category-grid]');
    initDragScroll(exploreCategoryGrid);

    // 2. Day nav links
    initDayNavLinks();

    // 3. Section limiting (Shopify sections render async via the theme;
    //    use MutationObserver to catch when all sections are in the DOM)
    var limitInitialized = initDateWiseSectionsLimit();

    if (!limitInitialized) {
      var attempts = 0;
      var maxAttempts = 20;
      var observer = new MutationObserver(function () {
        attempts++;
        var done = initDateWiseSectionsLimit();
        if (done || attempts >= maxAttempts) {
          observer.disconnect();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    // 5. Sticky date bar
    initStickyDateBar();

    // 4. Scrollbar thumbs
    initAllScrollbarThumbs();
    window.addEventListener('resize', initAllScrollbarThumbs, { passive: true });
    window.addEventListener('scroll', handleCardScroll, { capture: true, passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
